import React, { useState, useEffect, useCallback } from 'react';
import { useDebounce } from '../../hooks/useDebounce';
import { createPortal } from 'react-dom';
import { guestAPI } from '../../services/guestAPI';
import { useAppSelector } from '../../store/hooks';
import Input from '../UI/Input';
import Button from '../UI/Button';
import { logError } from '../../utils/errorLogger';

interface PreviousGuest {
  id: string;
  first_name: string;
  last_name: string;
  display_name: string;
  last_invited_date?: string;
  companionCount?: number;
}

interface CompanionHistoryItem {
  name: string;
  phone?: string;
  email?: string;
  relationship: string;
  last_used: string;
  event_count: number;
}

interface CompanionSettings {
  allow_companions: boolean;
  companion_limit?: number;
  selected_companions: string[]; // Array of companion names to import
}

interface PreviousGuestsModalProps {
  isOpen: boolean;
  onClose: () => void;
  eventId: string;
  onGuestsSelected: (guestIds: string[], companionSettings?: Map<string, CompanionSettings>) => void;
}

export const PreviousGuestsModal: React.FC<PreviousGuestsModalProps> = ({
  isOpen,
  onClose,
  eventId,
  onGuestsSelected,
}) => {
  const { theme } = useAppSelector((state) => state.ui);
  
  const [guests, setGuests] = useState<PreviousGuest[]>([]);
  const [loading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const [selectedGuests, setSelectedGuests] = useState<Set<string>>(new Set());
  const [isSearching, setIsSearching] = useState(false);
  const [companionSettings, setCompanionSettings] = useState<Map<string, CompanionSettings>>(new Map());
  const [showCompanionSettings, setShowCompanionSettings] = useState(false);
  const [fetchingCompanionHistory, setFetchingCompanionHistory] = useState<Set<string>>(new Set());
  const [companionHistoryMap, setCompanionHistoryMap] = useState<Map<string, CompanionHistoryItem[]>>(new Map());

  // Fetch previous guests
  const fetchPreviousGuests = useCallback(async (search?: string) => {
    setIsSearching(true);
    setError(null);
    
    try {
      const response = await guestAPI.getPreviousGuests(eventId, search);
      
      if (response.success) {
        setGuests(response.guests || []);
      } else {
        setError('Failed to load previous guests');
      }
    } catch (err: any) {
      logError('Error fetching previous guests', err);
      setError(err.response?.data?.error?.message || 'Failed to load previous guests');
    } finally {
      setIsSearching(false);
    }
  }, [eventId]);

  // Load guests on mount and when search changes
  useEffect(() => {
    if (isOpen && eventId) {
      const trimmedSearch = debouncedSearchQuery.trim();
      fetchPreviousGuests(trimmedSearch || undefined);
    }
  }, [isOpen, eventId, debouncedSearchQuery, fetchPreviousGuests]);

  // Handle search input
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  // Fetch companion history for a guest
  const fetchCompanionHistory = async (guestId: string) => {
    if (fetchingCompanionHistory.has(guestId)) return;
    
    setFetchingCompanionHistory(prev => new Set(prev).add(guestId));
    try {
      const history = await guestAPI.getCompanionHistory(guestId);
      const companionCount = history.companions?.length || 0;
      
      // Store full companion history
      setCompanionHistoryMap(prev => {
        const newMap = new Map(prev);
        newMap.set(guestId, history.companions || []);
        return newMap;
      });
      
      // Update guest with companion count
      setGuests(prev => prev.map(g => 
        g.id === guestId ? { ...g, companionCount } : g
      ));
      
      // Initialize companion settings if guest has companions
      if (companionCount > 0) {
        setCompanionSettings(prev => {
          const newMap = new Map(prev);
          if (!newMap.has(guestId)) {
            newMap.set(guestId, {
              allow_companions: false,
              companion_limit: undefined,
              selected_companions: []
            });
          }
          return newMap;
        });
      }
    } catch (err: any) {
      logError(`Failed to fetch companion history for guest ${guestId}`, err);
    } finally {
      setFetchingCompanionHistory(prev => {
        const newSet = new Set(prev);
        newSet.delete(guestId);
        return newSet;
      });
    }
  };

  // Toggle guest selection
  const toggleGuestSelection = (guestId: string) => {
    setSelectedGuests(prev => {
      const newSet = new Set(prev);
      if (newSet.has(guestId)) {
        newSet.delete(guestId);
        // Remove companion settings when deselected
        setCompanionSettings(prev => {
          const newMap = new Map(prev);
          newMap.delete(guestId);
          return newMap;
        });
      } else {
        newSet.add(guestId);
        // Fetch companion history when selected
        fetchCompanionHistory(guestId);
        // Initialize companion settings
        setCompanionSettings(prev => {
          const newMap = new Map(prev);
          if (!newMap.has(guestId)) {
            newMap.set(guestId, {
              allow_companions: false,
              companion_limit: undefined,
              selected_companions: []
            });
          }
          return newMap;
        });
      }
      return newSet;
    });
  };

  // Select all / deselect all
  const toggleSelectAll = () => {
    if (selectedGuests.size === guests.length) {
      setSelectedGuests(new Set());
    } else {
      setSelectedGuests(new Set(guests.map(g => g.id)));
    }
  };

  // Handle add selected guests
  const handleAddSelected = () => {
    if (selectedGuests.size === 0) {
      setError('Please select at least one guest');
      return;
    }
    
    const selectedArray = Array.from(selectedGuests);
    // Pass companion settings along with guest IDs
    onGuestsSelected(selectedArray, companionSettings);
  };

  // Update companion settings for a guest
  const updateCompanionSettings = (guestId: string, settings: Partial<CompanionSettings>) => {
    setCompanionSettings(prev => {
      const newMap = new Map(prev);
      const current = newMap.get(guestId) || {
        allow_companions: false,
        companion_limit: undefined,
        selected_companions: []
      };
      
      const updated = { ...current, ...settings };
      
      // If allow_companions is being set to false, clear selected companions
      if (settings.allow_companions === false) {
        updated.selected_companions = [];
      }
      
      newMap.set(guestId, updated);
      return newMap;
    });
  };

  // Toggle companion selection for a guest
  const toggleCompanionSelection = (guestId: string, companionName: string) => {
    setCompanionSettings(prev => {
      const newMap = new Map(prev);
      const current = newMap.get(guestId) || {
        allow_companions: false,
        companion_limit: undefined,
        selected_companions: []
      };
      
      const selected = new Set(current.selected_companions);
      if (selected.has(companionName)) {
        selected.delete(companionName);
      } else {
        // Check limit if set
        if (current.companion_limit && selected.size >= current.companion_limit) {
          return newMap; // Don't add if limit reached
        }
        selected.add(companionName);
      }
      
      newMap.set(guestId, {
        ...current,
        selected_companions: Array.from(selected)
      });
      return newMap;
    });
  };

  // Clear selection on close
  useEffect(() => {
    if (!isOpen) {
      setSelectedGuests(new Set());
      setSearchQuery('');
      setError(null);
      setCompanionSettings(new Map());
      setShowCompanionSettings(false);
      setCompanionHistoryMap(new Map());
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 z-[10001] flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div 
        className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col`}
      >
        {/* Header */}
        <div className={`flex items-center justify-between p-6 border-b ${
          theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <h2 className={`text-xl font-semibold ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>
            Select from Previous Guests
          </h2>
          <button
            onClick={onClose}
            className={`text-gray-500 hover:text-gray-700 ${
              theme === 'dark' ? 'hover:text-gray-300' : ''
            }`}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Search Bar */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex gap-2">
            <Input
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder="Search guests by name..."
              className="flex-1"
            />
          </div>
        </div>

        {/* Content */}
        <div className="p-4 max-h-[400px] overflow-y-auto">
          <style>{`
            .guest-list-container::-webkit-scrollbar {
              width: 6px;
            }
            .guest-list-container::-webkit-scrollbar-track {
              background: ${theme === 'dark' ? '#1f2937' : '#f3f4f6'};
              border-radius: 3px;
            }
            .guest-list-container::-webkit-scrollbar-thumb {
              background: ${theme === 'dark' ? '#4b5563' : '#d1d5db'};
              border-radius: 3px;
            }
            .guest-list-container::-webkit-scrollbar-thumb:hover {
              background: ${theme === 'dark' ? '#6b7280' : '#9ca3af'};
            }
          `}</style>
          <div className="guest-list-container">
          {loading || isSearching ? (
            <div className="flex justify-center items-center h-64">
              <div className="text-gray-500">Loading previous guests...</div>
            </div>
          ) : error ? (
            <div className={`text-center py-8 ${
              theme === 'dark' ? 'text-red-400' : 'text-red-600'
            }`}>
              {error}
            </div>
          ) : guests.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {searchQuery 
                ? 'No guests found matching your search' 
                : 'You haven\'t invited any guests to previous events yet'}
            </div>
          ) : (
            <>
              {/* Select All Control */}
              <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-200 dark:border-gray-700">
                <input
                  type="checkbox"
                  checked={selectedGuests.size === guests.length && guests.length > 0}
                  onChange={toggleSelectAll}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <label className={`${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Select All ({selectedGuests.size} selected)
                </label>
              </div>

              {/* Guest List */}
              <div className="space-y-2 max-h-[200px] overflow-y-auto pr-2">
                {guests.map((guest) => (
                  <div
                    key={guest.id}
                    className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                      selectedGuests.has(guest.id)
                        ? theme === 'dark' 
                          ? 'bg-blue-900/30 border-blue-700' 
                          : 'bg-blue-50 border-blue-300'
                        : theme === 'dark'
                          ? 'bg-gray-700/30 hover:bg-gray-700/50 border-gray-600'
                          : 'bg-gray-50 hover:bg-gray-100 border-gray-200'
                    } border`}
                    onClick={() => toggleGuestSelection(guest.id)}
                  >
                    <input
                      type="checkbox"
                      checked={selectedGuests.has(guest.id)}
                      onChange={() => toggleGuestSelection(guest.id)}
                      onClick={(e) => e.stopPropagation()}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <div className={`font-medium ${
                          theme === 'dark' ? 'text-white' : 'text-gray-900'
                        }`}>
                          {guest.display_name}
                        </div>
                        {guest.companionCount !== undefined && guest.companionCount > 0 && (
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            theme === 'dark' 
                              ? 'bg-purple-900/50 text-purple-300' 
                              : 'bg-purple-100 text-purple-700'
                          }`}>
                            {guest.companionCount} companion{guest.companionCount !== 1 ? 's' : ''}
                          </span>
                        )}
                        {fetchingCompanionHistory.has(guest.id) && (
                          <span className={`text-xs ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                          }`}>
                            Loading...
                          </span>
                        )}
                      </div>
                      {guest.last_invited_date && (
                        <div className={`text-sm ${
                          theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                          Last invited: {new Date(guest.last_invited_date).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
          </div>
        </div>

        {/* Companion Settings Section */}
        {selectedGuests.size > 0 && (
          <div className={`p-4 border-t ${
            theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
          }`}>
            <div className="flex items-center justify-between mb-3">
              <h3 className={`text-sm font-semibold ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                Companion Settings
              </h3>
              <button
                onClick={() => setShowCompanionSettings(!showCompanionSettings)}
                className={`text-sm ${
                  theme === 'dark' ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'
                }`}
              >
                {showCompanionSettings ? 'Hide' : 'Show'}
              </button>
            </div>
            
            {showCompanionSettings && (
              <div className="space-y-4">
                {Array.from(selectedGuests).map(guestId => {
                  const guest = guests.find(g => g.id === guestId);
                  const settings = companionSettings.get(guestId) || {
                    allow_companions: false,
                    companion_limit: undefined,
                    selected_companions: []
                  };
                  const companionHistory = companionHistoryMap.get(guestId) || [];
                  const isFetchingHistory = fetchingCompanionHistory.has(guestId);
                  
                  return (
                    <div key={guestId} className={`p-3 rounded-lg ${
                      theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-50'
                    }`}>
                      <div className={`font-medium mb-2 ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                      }`}>
                        {guest?.display_name || 'Guest'}
                        {guest?.companionCount !== undefined && guest.companionCount > 0 && (
                          <span className={`ml-2 text-xs ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                          }`}>
                            ({guest.companionCount} companion{guest.companionCount !== 1 ? 's' : ''} available)
                          </span>
                        )}
                      </div>
                      
                      <div className="space-y-2">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={settings.allow_companions}
                            onChange={(e) => updateCompanionSettings(guestId, {
                              allow_companions: e.target.checked
                            })}
                            className="mr-2"
                          />
                          <span className={`text-sm ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                          }`}>
                            Allow Companions
                          </span>
                        </label>
                        
                        {settings.allow_companions && (
                          <>
                            <div className="flex items-center gap-2">
                              <label className={`text-sm ${
                                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                              }`}>
                                Limit:
                              </label>
                              <input
                                type="number"
                                min="1"
                                max="10"
                                value={settings.companion_limit || ''}
                                onChange={(e) => {
                                  const limit = e.target.value ? parseInt(e.target.value) : undefined;
                                  updateCompanionSettings(guestId, {
                                    companion_limit: limit
                                  });
                                  // Trim selected companions if limit is reduced
                                  if (limit && settings.selected_companions.length > limit) {
                                    updateCompanionSettings(guestId, {
                                      selected_companions: settings.selected_companions.slice(0, limit)
                                    });
                                  }
                                }}
                                className={`w-20 px-2 py-1 text-sm border rounded ${
                                  theme === 'dark'
                                    ? 'border-gray-600 bg-gray-700 text-white'
                                    : 'border-gray-300 bg-white text-gray-900'
                                }`}
                                placeholder="No limit"
                              />
                            </div>
                            
                            {/* Companion Selection */}
                            {guest?.companionCount !== undefined && guest.companionCount > 0 && (
                              <div className="mt-3">
                                <div className={`text-sm font-medium mb-2 ${
                                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                }`}>
                                  Select Companions to Import ({settings.selected_companions.length} selected)
                                  {settings.companion_limit && (
                                    <span className={`ml-2 text-xs ${
                                      theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
                                    }`}>
                                      (Limit: {settings.companion_limit})
                                    </span>
                                  )}
                                </div>
                                
                                {isFetchingHistory ? (
                                  <div className={`text-xs ${
                                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                  }`}>
                                    Loading companions...
                                  </div>
                                ) : companionHistory.length > 0 ? (
                                  <div className={`space-y-2 max-h-48 overflow-y-auto border rounded p-2 ${
                                    theme === 'dark' ? 'border-gray-600' : 'border-gray-200'
                                  }`}>
                                    {companionHistory.map((companion) => {
                                      const isSelected = settings.selected_companions.includes(companion.name);
                                      const isDisabled = settings.companion_limit 
                                        ? !isSelected && settings.selected_companions.length >= settings.companion_limit
                                        : false;
                                      
                                      return (
                                        <label
                                          key={companion.name}
                                          className={`flex items-start gap-2 p-2 rounded cursor-pointer ${
                                            isDisabled
                                              ? 'opacity-50 cursor-not-allowed'
                                              : isSelected
                                                ? theme === 'dark' ? 'bg-blue-900/30' : 'bg-blue-50'
                                                : theme === 'dark' ? 'hover:bg-gray-600/50' : 'hover:bg-gray-100'
                                          }`}
                                        >
                                          <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={() => !isDisabled && toggleCompanionSelection(guestId, companion.name)}
                                            disabled={isDisabled}
                                            className="mt-1"
                                          />
                                          <div className="flex-1">
                                            <div className={`text-sm font-medium ${
                                              theme === 'dark' ? 'text-white' : 'text-gray-900'
                                            }`}>
                                              {companion.name}
                                            </div>
                                            <div className={`text-xs ${
                                              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                            }`}>
                                              {companion.relationship}
                                              {companion.phone && ` • ${companion.phone}`}
                                              {companion.email && ` • ${companion.email}`}
                                              {companion.event_count > 0 && (
                                                <span className="ml-1">• Used in {companion.event_count} event{companion.event_count !== 1 ? 's' : ''}</span>
                                              )}
                                            </div>
                                          </div>
                                        </label>
                                      );
                                    })}
                                  </div>
                                ) : (
                                  <div className={`text-xs ${
                                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                  }`}>
                                    No companion history available
                                  </div>
                                )}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className={`flex justify-end gap-3 p-4 border-t ${
          theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <Button
            onClick={onClose}
            variant="outline"
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddSelected}
            disabled={selectedGuests.size === 0}
          >
            Add Selected ({selectedGuests.size})
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
};

